import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout, FormNotice } from './AuthLayout';
import { apiMessage } from '../lib/api';
import { finishGoogleConnection, takePending } from '../datasource/google';

/** States already posted by this page, so a double effect cannot spend one twice. */
const posted = new Set<string>();

/**
 * `/connect/google` — where Google sends the browser back after consent.
 *
 * The code and state are taken out of the address bar at once (so they are
 * not in the history, a bookmark, or a referrer) and posted to the server
 * **with the signed-in person's own session**. The server finishes only a
 * state this person started for this company (0030). This page also finishes
 * only a connection this tab started: a stranger's link that lands here has
 * no note in this tab, and is not posted at all.
 */
export function ConnectGoogle() {
    const navigate = useNavigate();
    const [problem, setProblem] = useState<string | null>(null);
    const [back, setBack] = useState<string | null>(null);
    const handled = useRef(false);

    useEffect(() => {
        if (handled.current) return;
        handled.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        // Out of the address bar before anything else happens.
        window.history.replaceState(null, '', window.location.pathname);

        const pending = state ? takePending(state) : null;
        if (!state || !pending) {
            setProblem('This Google sign-in was not started from this tab, or it is too old. Start again from the data source screen.');
            return;
        }
        const home = `/w/${encodeURIComponent(pending.slug)}/${pending.kind === 'stock' ? 'stock' : 'data'}`;
        setBack(home);

        if (error) {
            setProblem(error === 'access_denied'
                ? 'You cancelled at Google, so nothing was connected.'
                : 'Google did not complete the connection. Nothing was connected; try again.');
            return;
        }
        if (!code || posted.has(state)) return;
        posted.add(state);

        finishGoogleConnection(pending.workspaceId, code, state)
            .then(() => navigate(`${home}?google=connected`, { replace: true }))
            .catch(failed => setProblem(failed instanceof Error ? failed.message : apiMessage(failed)));
    }, [navigate]);

    return (
        <AuthLayout title="Connecting Google">
            {problem ? (
                <>
                    <FormNotice tone="error">{problem}</FormNotice>
                    <p className="mt-5 text-[14px]">
                        <Link to={back ?? '/'} className="text-accent font-semibold hover:underline">
                            {back ? 'Back to the data source' : 'Back to Timely Payment'}
                        </Link>
                    </p>
                </>
            ) : (
                <p role="status" className="text-[14px] text-label-2">Finishing the connection…</p>
            )}
        </AuthLayout>
    );
}
